import type { Scene } from '@/types/scene.types';

/**
 * Fetches all available scenes from the API.
 *
 * @returns Promise resolving to an array of scene data
 */
export async function fetchScenes(): Promise<Scene[]> {
  // Use cache: 'no-cache' to always get fresh scene list from API
  // This is important because the scene list can change when files are uploaded to GCS
  const response = await fetch('/api/scenes', {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch scenes: ${response.statusText}`);
  }

  const data = (await response.json()) as Scene[];
  return data;
}

/**
 * Fetches a single scene by its slug from the API.
 *
 * @param slug - The scene slug (e.g., 'ithor-bundled-small_w_robot')
 * @returns Promise resolving to the scene data
 */
export async function fetchScene(slug: string): Promise<Scene> {
  const response = await fetch(`/api/scenes/${slug}`, {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch scene '${slug}': ${response.statusText}`);
  }

  const data = (await response.json()) as Scene;
  return data;
}

/**
 * Fetches the actions JSON for a scene.
 * Actions JSON contains all available actions/objects for the scene,
 * including robot poses, camera positions, and action sequences.
 *
 * @param actionsJsonFile - Filename of the actions JSON (from scene.actionsJsonFile)
 * @returns Promise resolving to the actions data
 */
export async function fetchSceneActions(actionsJsonFile: string): Promise<any> {
  // Actions JSON files are served through the API
  const response = await fetch(`/api/scenes/actions/${actionsJsonFile}`, {
    cache: 'default', // Can cache actions as they don't change frequently
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch actions file '${actionsJsonFile}': ${response.statusText}`);
  }

  const data = await response.json();
  return data;
}
